import type {DocumentReference, QueryDocumentSnapshot} from 'firebase/firestore'
import type {CourseDocument} from '@goschool/model'
import {useGoSchoolFunctions} from '@goschool/model'
import type {SvgIconProps} from '@mui/material'
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography
} from '@mui/material'
import {Trans, useTranslation} from 'react-i18next'
import {useCallback, useMemo, useState} from 'react'
import {GoSchool} from '@goschool/routing'
import LibraryMusicIcon from '@mui/icons-material/LibraryMusic'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import QuizIcon from '@mui/icons-material/Quiz'
import DeleteIcon from '@mui/icons-material/Delete'
import {useUserContext} from '@goschool/auth'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import {useConfirm} from '@goschool/components'
import {getDocumentState, type StateProps} from '../documentState'

export function CourseDocumenList({courseDocuments}: { courseDocuments: QueryDocumentSnapshot<CourseDocument>[]; }) {
  return <Paper>
    <List>
      {courseDocuments.map((documentSnapshot, i) =>
        <CourseDocumenItem
          key={documentSnapshot.id}
          documentSnapshot={documentSnapshot}
          document={documentSnapshot.data()}
          isLast={i === courseDocuments.length - 1}
        />
      )}
    </List>
  </Paper>
}

function CourseDocumenItem({
  documentSnapshot,
  document,
  isLast
}: {
  documentSnapshot: QueryDocumentSnapshot<CourseDocument>;
  document: CourseDocument;
  isLast: boolean;
}) {
  const metadata = document.metadata ?? undefined
  const authors = useMemo(() => {
    const authorList = document.authors ?? metadata?.authors ?? metadata?.speakers
    if (authorList == null || authorList.length === 0) {
      return null
    } else {
      return authorList.join(', ')
    }
  }, [document.authors, metadata])
  const secondaryText =
    document.lead ??
    metadata?.lead ??
    document.abstract ??
    metadata?.abstract ??
    null
  const [isDeleting, setIsDeleting] = useState(false)
  const state = useDocumentState(document)
  return <>
    <ListItem
      alignItems="flex-start"
      secondaryAction={
        <CourseDocumentActions
          documentSnaphot={documentSnapshot}
          isDeleting={isDeleting}
          onDeleteStart={() => setIsDeleting(true)}
          onDeleteError={() => setIsDeleting(false)}
        />
      }
    >
      <ListItemIcon>
        <CourseDocumentIcon document={document} color="info"/>
      </ListItemIcon>
      <ListItemText
        slotProps={{
          primary: {variant: 'body1'},
          secondary: {variant: 'caption'}
        }}
        primary={
          <>
            <Link href={GoSchool.document(documentSnapshot.ref)}>
              {document.title}
            </Link>
            {authors != null ? <Typography variant="caption"> {authors}</Typography> : null}
          </>
        }
        secondary={
          isDeleting ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={14}/>
              <Typography variant="caption">
                <Trans i18nKey="document:actions.deleting"/>
              </Typography>
            </Stack>
          ) : state.status === 'succeeded' ? secondaryText : <DocumentState state={state}/>
        }
      />
    </ListItem>
    {!isLast && <Divider variant="middle" component="li"/>}
  </>
}

function CourseDocumentActions({
  documentSnaphot,
  isDeleting,
  onDeleteStart,
  onDeleteError
}: {
  documentSnaphot: QueryDocumentSnapshot<CourseDocument>;
  isDeleting: boolean;
  onDeleteStart: () => void;
  onDeleteError: () => void;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }
  return <>
    <IconButton onClick={handleClick} disabled={isDeleting}>
      {isDeleting ? <CircularProgress size={18}/> : <MoreHorizIcon/>}
    </IconButton>
    <Menu open={anchorEl != null} onClose={handleClose} anchorEl={anchorEl}
      transformOrigin={{horizontal: 'right', vertical: 'top'}}
      anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}>
      <DeleteDocumentMenuItem
        documentRef={documentSnaphot.ref}
        handleClose={handleClose}
        onDeleteStart={onDeleteStart}
        onDeleteError={onDeleteError}
      />
    </Menu>
  </>
}

function DeleteDocumentMenuItem({
  documentRef, handleClose, onDeleteStart, onDeleteError
}: {
  documentRef: DocumentReference<CourseDocument>,
  handleClose: () => void,
  onDeleteStart: () => void,
  onDeleteError: () => void
}) {
  const {roles} = useUserContext()

  const {deleteDocument} = useGoSchoolFunctions()
  const {t} = useTranslation()

  const confirm = useConfirm()


  const handleDelete = useCallback(
    () => {
      handleClose()

      return confirm(
        async () => {
          onDeleteStart()
          try {
            await deleteDocument(documentRef)
          } catch (error) {
            onDeleteError()
            throw error
          }
        },
        t('document:actions.confirm-delete')
      )
    },
    [confirm, deleteDocument, documentRef, handleClose, onDeleteError, onDeleteStart, t]
  )

  if (roles.includes('admin') || roles.includes('instructor')) {
    return <MenuItem onClick={handleDelete}><DeleteIcon
      fontSize="small"/><ListItemText><Trans i18nKey="document:actions.delete"/></ListItemText></MenuItem>
  }
}

function CourseDocumentIcon({document, ...iconProps}: { document: CourseDocument } & Omit<SvgIconProps, 'fontSize'>) {
  if (document.content_type === 'application/pdf') {
    return <PictureAsPdfIcon fontSize="large" {...iconProps}/>
  } else if (
    document.content_type?.startsWith('audio/') ||
    document.content_type?.startsWith('video/')
  ) {
    return <LibraryMusicIcon fontSize="large" {...iconProps}/>
  } else {
    return <QuizIcon fontSize="large" {...iconProps}/>
  }
}


export function DocumentState({state}: { state: StateProps }) {
  if (state.status === 'succeeded') {
    return null
  }

  switch (state.transition) {
  case 'ingestion':
    return <State {...state} steps={1}/>
  case 'quick_processing':
    return <State {...state} steps={1}/>
  case 'visual_processing':
    return <State {...state} steps={1}/>
  case 'finalizing':
    return <State {...state} steps={1}/>
  case 'visual_refinement':
    return <State {...state} steps={1}/>
  case 'pending':
    return <State {...state} steps={4}/>
  case 'preprocess_upload':
    return <State {...state} steps={4}/>
  case 'extract_content':
    return <State {...state} steps={4}/>
  case 'transcribe_audio':
    return <State {...state} steps={4}/>
  case 'content_analysis':
    return <State {...state} steps={4}/>
  default:
    return <State {...state} steps={4}/>
  }
}

export function useDocumentState(document: CourseDocument): StateProps {
  return useMemo(() => getDocumentState(document), [document])
}

function State(props: StateProps & { steps: number }) {
  const {transition, progress, total, status, step, steps} = props
  const {t} = useTranslation()
  const value = useMemo(() => {
    if (progress != null && total != null && total > 0) {
      return (100 * step + (100 * progress) / total) / steps
    }
    return (100 * step + ((progress ?? (status === 'succeeded' ? 100 : 0)))) / steps
  }, [progress, total, status, step, steps])

  return <Stack direction="row" alignItems="center" justifyContent="flex-start">
    <Box width={250} mr={1}>
      <LinearProgress
        color={status === 'failed' ? 'error' : 'info'}
        variant="determinate"
        value={value}
      />
    </Box>
    <Typography variant="caption">
      {t(props.label ?? `document:status.${transition}`)}
      {total != null ? ` ${progress}/${total}` : ''}
    </Typography>
  </Stack>
}
